if (!customElements.get('product-as-variant')) {
  customElements.define(
    'product-as-variant',
    class ProductAsVariant extends HTMLElement {
      static cache = new Map();
      static delegated = false;
      static abortController = null;
      static injectedScripts = new Set();

      connectedCallback() {
        // Pills live inside the region that gets replaced, so the click handler is
        // delegated once on document and never re-bound per instance.
        if (ProductAsVariant.delegated) { return; }
        ProductAsVariant.delegated = true;
        document.addEventListener('click', ProductAsVariant.onDocumentClick);
      }

      static onDocumentClick(event) {
        const pill = event.target.closest('[data-product-as-variant-pill]');
        if (!pill) { return; }
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) { return; }

        const url = pill.dataset.productUrl;
        if (!url || pill.classList.contains('is-selected')) { return; }

        event.preventDefault();
        ProductAsVariant.selectProduct(url, pill);
      }

      static async selectProduct(url, pill) {
        const main = document.getElementById('MainContent');
        if (!main) { return; }

        for (const other of document.querySelectorAll('[data-product-as-variant-pill]')) {
          other.classList.toggle('is-selected', other === pill);
          if (other === pill) {
            other.setAttribute('aria-current', 'true');
          } else {
            other.removeAttribute('aria-current');
          }
        }

        main.classList.add('product-as-variant--loading');

        const wasFocusInMain = main.contains(document.activeElement);
        const focusedPillUrl = document.activeElement
          ?.closest?.('[data-product-as-variant-pill]')
          ?.dataset.productUrl;

        // The swap builds a brand-new sticky-atc, which mounts with default state
        // before its observers run: the panel would close and the bar would slide
        // out of view and back. Carry both across and apply them pre-insertion.
        const stickyOptionsWereOpen = Boolean(
          main.querySelector('.sticky-atc__options-toggle')?.open,
        );
        const outgoingSticky = main.querySelector('sticky-atc');
        const stickyWasVisible = Boolean(
          outgoingSticky?.classList.contains('sticky-atc--visible'),
        );
        // The class alone is not enough: it is derived from these two flags, and a
        // fresh instance defaults both to false. Its footer IntersectionObserver
        // reports back before the media one does, and that first
        // updateStickyVisibility() would strip the class again.
        const stickyMediaPastThreshold = Boolean(outgoingSticky?.isMediaPastThreshold);
        const stickyFooterVisible = Boolean(outgoingSticky?.isFooterVisible);

        try {
          const html = await ProductAsVariant.fetchPage(url);
          if (!html) { return; }

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.getElementById('MainContent');
          if (!incoming) { throw new Error('MainContent missing in fetched page'); }

          // Set these before insertion: the new sticky-atc reads its own markup as
          // it upgrades, so changing them afterwards would visibly flicker.
          if (stickyOptionsWereOpen) {
            incoming.querySelector('.sticky-atc__options-toggle')?.setAttribute('open', '');
          }
          const incomingSticky = incoming.querySelector('sticky-atc');
          if (incomingSticky) {
            if (stickyWasVisible) {
              incomingSticky.classList.add('sticky-atc--visible');
            }
            incomingSticky.setAttribute('data-media-past-threshold', String(stickyMediaPastThreshold));
            incomingSticky.setAttribute('data-footer-visible', String(stickyFooterVisible));
          }

          main.innerHTML = incoming.innerHTML;

          ProductAsVariant.restoreFocus(main, focusedPillUrl, wasFocusInMain);

          const title = doc.querySelector('title')?.textContent;
          // eslint-disable-next-line require-atomic-updates -- no await follows this read
          if (title) { document.title = title; }

          window.history.replaceState({}, '', url);

          ProductAsVariant.afterSwap(main, doc);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('[product-as-variant]', error);
            window.location.href = url;
          }
        } finally {
          main.classList.remove('product-as-variant--loading');
        }
      }

      static async fetchPage(url) {
        if (ProductAsVariant.cache.has(url)) { return ProductAsVariant.cache.get(url); }

        ProductAsVariant.abortController?.abort();
        ProductAsVariant.abortController = new AbortController();

        const response = await fetch(url, {signal: ProductAsVariant.abortController.signal});
        if (!response.ok) { throw new Error(`HTTP ${response.status}`); }

        const html = await response.text();
        ProductAsVariant.cache.set(url, html);
        return html;
      }

      static afterSwap(main, doc) {
        ProductAsVariant.injectMissingScripts(main);
        ProductAsVariant.removeForeignSectionRenderer(main);

        const {variant} = ProductAsVariant.readProductJson(main);

        for (const section of main.querySelectorAll('.shopify-section')) {
          section.dispatchEvent(
            new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: {sectionId: section.id},
            }),
          );
        }

        document.dispatchEvent(
          new CustomEvent('renderSection:complete', {
            detail: {sectionId: null, variant, html: doc},
          }),
        );

        ProductAsVariant.syncStickyMedia(main);
        ProductAsVariant.trackViewedProduct(main);
        ProductAsVariant.announce(main);
      }

      static restoreFocus(main, focusedPillUrl, wasFocusInMain) {
        if (!wasFocusInMain) { return; }

        const target =
          (focusedPillUrl &&
            main.querySelector(
              `[data-product-as-variant-pill][data-product-url="${CSS.escape(focusedPillUrl)}"]`,
            )) ||
          main.querySelector('[data-product-as-variant-pill].is-selected') ||
          main.querySelector('h1');
        if (!target) { return; }

        // Pills are anchors and already focusable; the heading fallback is not.
        if (!target.hasAttribute('href') && !target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        // The swap keeps the scroll position — let focus follow it rather than
        // scrolling the page a second time.
        target.focus({preventScroll: true});
      }

      static announce(main) {
        const status = document.getElementById('ProductAsVariantStatus');
        if (!status) { return; }

        const title = main.querySelector('h1')?.textContent.trim();
        if (!title) { return; }

        status.textContent = status.dataset.message.replace('[title]', title);
      }

      // The gallery dispatches product-media::updated from its connectedCallback,
      // but it upgrades before the sticky bar further down the markup has
      // attached its listener, so that first event is always missed.
      static syncStickyMedia(main) {
        const slide =
          main.querySelector('.product-media-gallery-item.swiper-slide-active:not(.hidden)') ||
          main.querySelector('.product-media-gallery-item:not(.hidden)');
        const image = slide?.querySelector('img');
        const stickyImage = main.querySelector('[data-sticky-img]');
        if (!image || !stickyImage) { return; }

        stickyImage.src = image.currentSrc || image.src;
        if (image.alt) { stickyImage.alt = image.alt; }
      }

      // Consumers of renderSection:complete (sticky ATC, skio, heading blocks)
      // read detail.variant. The section emits [data-selected-variant] in both
      // the variant_picker and product_as_variant blocks, so prefer this
      // block's own copy.
      static readProductJson(main) {
        const variantJson = (
          main.querySelector('product-as-variant [data-selected-variant]') ||
          main.querySelector('[data-selected-variant]')
        )?.textContent;

        let variant = null;
        try {
          if (variantJson) { variant = JSON.parse(variantJson); }
        } catch (error) {
          console.error('[product-as-variant] selected variant JSON parse failed', error);
        }

        return {variant};
      }

      // Scripts inside the replaced region never execute from innerHTML, and a tag
      // that was still pending when the swap happened (module/defer scripts during
      // initial load) is destroyed before it ever runs. Presence in the document is
      // therefore not proof of execution, so re-inject into <head> — which this
      // element tracks — unless we already put that src there ourselves. Duplicate
      // execution is safe: the theme's components guard on customElements.get().
      // jQuery/fancybox stay excluded because re-running them wipes plugin state.
      static injectMissingScripts(main) {
        for (const script of main.querySelectorAll('script[src]')) {
          const {src} = script;
          if (!src || ProductAsVariant.injectedScripts.has(src)) { continue; }
          if (/jquery|fancybox/i.test(src)) { continue; }

          const replacement = document.createElement('script');
          replacement.src = src;
          // Without this a `type="module"` file loads as a classic script and
          // throws on its import/export statements.
          if (script.type) { replacement.type = script.type; }
          if (script.async) { replacement.async = true; }
          if (script.noModule) { replacement.noModule = true; }
          replacement.defer = true;

          document.head.appendChild(replacement);
          ProductAsVariant.injectedScripts.add(src);
        }
      }

      // No Bazaarvoice re-init here on purpose. This store runs Conversations
      // Display v2 (bv.js), which is declarative: it discovers
      // [data-bv-show] containers itself, including ones inserted after load
      // (collection filtering relies on exactly that). The legacy
      // $BV.ui('rr', ...) API belongs to v1 (bvapi.js) and does not exist here,
      // and touching these containers — assigning ids or clearing innerHTML —
      // races BV's own observer and stops the widgets rendering at all.

      // cross-product-section-rendering listens for renderSection:complete and
      // would re-render Bazaarvoice plus a stale cross-referenced product's
      // sections over the page this element just swapped in. Drop it so this
      // element stays the single owner of post-swap rendering; the freshly
      // fetched markup brings its own copy for the variant_picker flow.
      static removeForeignSectionRenderer(main) {
        for (const element of main.querySelectorAll('cross-product-section-rendering')) {
          element.remove();
        }
      }

      static trackViewedProduct(main) {
        const payload = main.querySelector('[data-klaviyo-viewed-product]')?.textContent;
        if (!payload) { return; }

        try {
          const item = JSON.parse(payload);
          window._learnq = window._learnq || [];
          window._learnq.push(['track', 'Viewed Product', item]);
        } catch (error) {
          console.error('[product-as-variant] Klaviyo payload parse failed', error);
        }
      }
    },
  );
}
